<template>
  <div class="card p-shadow-app">
    <Toast />
    <Loading :active.sync="loading" :isFullPage="false" />

    <div class="p-mb-4 p-d-flex p-jc-between p-ai-center">
      <div>
        <h3 class="p-m-0">
          {{ isEditMode ? `Editar Compra - Nº ${form.nfact || ''}` : 'Nova Compra' }}
        </h3>
        <small class="p-d-block p-mt-1 text-secondary">Preencha o cabeçalho e as linhas do documento de compra.</small>
      </div>
      <span class="p-tag p-tag-warning p-px-3 p-py-2" style="font-size: 1rem;" v-if="form.status !== 'Regularizado'">
        {{ form.status || 'Por Regularizar.' }}
      </span>
      <span class="p-tag p-tag-success p-px-3 p-py-2" style="font-size: 1rem;" v-else>
        {{ form.status }}
      </span>
    </div>

    <form @submit.prevent="submitForm">
      <div class="p-fluid p-formgrid p-grid p-mb-3">
        <div class="p-field p-col-12 p-md-5">
          <label for="supplier">Fornecedor *</label>
          <AppAutocomplete
            fieldId="supplier"
            v-model="form.no"
            :optionLabel="getSupplierLabel"
            optionValue="id"
            :editModel="getCurrentSupplier"
            :fetchFunction="fetchSuppliers"
            placeholder="Selecionar Fornecedor"
            @selection-change="onSupplierChange"
            @clear-selection="clearSupplier"
            appendTo="body"
            required
          >
            <template #option="slotProps">
              <div>
                <b>#{{ slotProps.option.id }} - {{ slotProps.option.name }}</b><br />
                <span v-if="slotProps.option.nif">NIF: {{ slotProps.option.nif }}</span>
              </div>
            </template>
          </AppAutocomplete>
          <small v-if="formErrors.no" class="p-error">{{ formErrors.no }}</small>
        </div>

        <!-- Documento Type -->
        <div class="p-field p-col-12 p-md-3">
          <label for="nmdoc">Documento *</label>
          <Dropdown
            id="nmdoc"
            v-model="form.nmdoc"
            :options="docTypes"
            optionLabel="name"
            optionValue="name"
            placeholder="Tipo Doc."
            @change="onDocTypeChange"
            :class="{ 'p-invalid': formErrors.nmdoc }"
          />
          <small v-if="formErrors.nmdoc" class="p-error">{{ formErrors.nmdoc }}</small>
        </div>

        <!-- Document Nº -->
        <div class="p-field p-col-12 p-md-2">
          <label for="nfact">Nº Documento *</label>
          <InputText
            id="nfact"
            v-model="form.nfact"
            placeholder="Ex: 1276000123445"
            :class="{ 'p-invalid': formErrors.nfact }"
          />
          <small v-if="formErrors.nfact" class="p-error">{{ formErrors.nfact }}</small>
        </div>

        <!-- Data Interna -->
        <div class="p-field p-col-12 p-md-2">
          <label for="dataopen">Data Interna *</label>
          <Calendar
            id="dataopen"
            v-model="form.dataopen"
            dateFormat="yy-mm-dd"
            :showIcon="true"
          />
        </div>
      </div>

      <!-- TABS SECTION -->
      <TabView class="p-mb-4">
        <!-- Tab 1: Página Principal -->
        <TabPanel header="Página Principal">
          <div class="p-fluid p-formgrid p-grid p-pt-2">
            <div class="p-field p-col-12 p-md-2">
              <label for="moeda">Moeda</label>
              <Dropdown
                id="moeda"
                v-model="form.moeda"
                :options="currencies"
                optionLabel="name"
                optionValue="id"
                placeholder="EURO"
              />
            </div>

            <div class="p-field p-col-12 p-md-2">
              <label for="investimento">Investimento</label>
              <Dropdown
                id="investimento"
                v-model="form.investimento"
                :options="investments"
                optionLabel="name"
                optionValue="id"
                placeholder="Nao"
              />
            </div>

            <div class="p-field p-col-12 p-md-2">
              <label for="datadoc">Data Original</label>
              <Calendar
                id="datadoc"
                v-model="form.datadoc"
                dateFormat="yy-mm-dd"
                :showIcon="true"
              />
            </div>

            <div class="p-field p-col-12 p-md-2">
              <label for="vencimento">Vencimento</label>
              <Calendar
                id="vencimento"
                v-model="form.vencimento"
                dateFormat="yy-mm-dd"
                :showIcon="true"
              />
            </div>
          </div>
        </TabPanel>

        <!-- Tab 2: Ficheiros -->
        <TabPanel header="Ficheiros">
          <PurchaseFiles :stamp="stamp" :stagedFiles.sync="stagedFiles" />
        </TabPanel>
      </TabView>

      <!-- LINES TABLE SECTION -->
      <div class="p-mb-3">
        <div class="p-d-flex p-jc-between p-ai-center p-mb-2">
          <h4 class="p-m-0"><i class="pi pi-list p-mr-2"></i>Linhas da Compra</h4>
        </div>

        <div class="p-table-responsive">
          <table class="p-datatable-table custom-purchases-table">
            <thead>
              <tr>
                <th style="min-width: 220px;">Referência</th>
                <th style="min-width: 220px;">Designação</th>
                <th style="min-width: 90px;" class="text-right">Quant.</th>
                <th style="min-width: 100px;" class="text-right">Preço</th>
                <th style="min-width: 75px;" class="text-right">Desc.1</th>
                <th style="min-width: 75px;" class="text-right">Desc.2</th>
                <th style="min-width: 100px;" class="text-right">Total</th>
                <th style="min-width: 180px;">Armazém</th>
                <th style="min-width: 110px;" class="text-center">IVA</th>
                <th style="min-width: 75px;" class="text-center">IVA Incl.</th>
                <th style="min-width: 60px;" class="text-center">Desp.</th>
                <th style="min-width: 50px;" class="text-center"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(line, index) in form.lines" :key="index">
                <td>
                  <AutoComplete
                    v-model="line.ref"
                    @complete="searchProduct"
                    :suggestions="productSuggestions"
                    field="code"
                    placeholder="Ref."
                    @item-select="(e) => onProductSelect(e.value, index)"
                    class="p-inputtext-sm p-w-100"
                    :dropdown="true"
                    appendTo="body"
                  >
                    <template #item="itemSlot">
                      <div>
                        <strong>{{ itemSlot.item.code }}</strong> - {{ itemSlot.item.description }}
                      </div>
                    </template>
                  </AutoComplete>
                </td>
                <td>
                  <InputText v-model="line.design" placeholder="Designação" class="p-inputtext-sm p-w-100" />
                </td>
                <td>
                  <InputNumber
                    v-model="line.qtt"
                    :min="0.001"
                    :minFractionDigits="2"
                    :maxFractionDigits="2"
                    class="p-inputtext-sm text-right"
                    @input="recalculateTotals"
                  />
                </td>
                <td>
                  <InputNumber
                    v-model="line.preco"
                    :min="0"
                    :minFractionDigits="2"
                    :maxFractionDigits="2"
                    class="p-inputtext-sm text-right"
                    @input="recalculateTotals"
                  />
                </td>
                <td>
                  <InputNumber
                    v-model="line.desconto"
                    :min="0"
                    :max="100"
                    :minFractionDigits="0"
                    :maxFractionDigits="2"
                    class="p-inputtext-sm text-right"
                    @input="recalculateTotals"
                  />
                </td>
                <td>
                  <InputNumber
                    v-model="line.desc2"
                    :min="0"
                    :max="100"
                    :minFractionDigits="0"
                    :maxFractionDigits="2"
                    class="p-inputtext-sm text-right"
                    @input="recalculateTotals"
                  />
                </td>
                <td class="text-right font-weight-bold p-vertical-align-middle">
                  {{ formatCurrency(computeLineTotal(line)) }}
                </td>
                <td>
                  <Dropdown
                    v-model="line.armazem"
                    :options="warehouses"
                    dataKey="reference"
                    optionLabel="name"
                    optionValue="reference"
                    filter
                    filterBy="reference,name"
                    placeholder="Selecionar armazém"
                    :virtualScrollerOptions="{ itemSize: 38 }"
                    appendTo="body"
                    class="p-inputtext-sm p-w-100"
                  >
                    <template #value="slotProps">
                      <span v-if="slotProps.value">
                        {{ getWarehouseLabel(slotProps.value) }}
                      </span>
                      <span v-else>Selecionar armazém</span>
                    </template>
                    <template #option="slotProps">
                      <div>
                        <strong>{{ slotProps.option.reference }}</strong> -
                        {{ slotProps.option.name }}
                      </div>
                    </template>
                  </Dropdown>
                </td>
                <td>
                  <Dropdown
                    v-model="line.iva"
                    :options="listVats"
                    optionLabel="value"
                    optionValue="code"
                    dataKey="code"
                    placeholder="IVA"
                    class="p-inputtext-sm p-w-100"
                    appendTo="body"
                    @change="onVatChange(line)"
                  >
                    <template #option="slotProps">
                      <div>
                        <strong>{{ slotProps.option.value }}%</strong>
                        <span v-if="slotProps.option.description" class="text-secondary" style="font-size: 0.8rem;"> ({{ slotProps.option.description }})</span>
                      </div>
                    </template>
                  </Dropdown>
                </td>
                <td class="text-center p-vertical-align-middle">
                  <Checkbox v-model="line.ivaincl" :binary="true" @change="recalculateTotals" />
                </td>
                <td class="text-center p-vertical-align-middle">
                  <Checkbox v-model="line.desp" :binary="true" />
                </td>
                <td class="text-center p-vertical-align-middle">
                  <Button
                    icon="pi pi-times"
                    class="p-button-danger p-button-text p-button-sm p-p-0"
                    @click="removeLine(index)"
                    type="button"
                    tooltip="Remover Linha"
                  />
                </td>
              </tr>
              <tr v-if="form.lines.length === 0">
                <td colspan="13" class="text-center p-p-4 text-secondary">
                  Nenhuma linha adicionada. Clique abaixo para adicionar linhas.
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- LINE ACTIONS BAR -->
        <div class="p-d-flex p-ai-center p-mt-2">
          <Button
            label="Adicionar Linha"
            icon="pi pi-plus"
            class="p-button-sm p-mt-2 p-mr-2"
            type="button"
            @click="addLine"
          />
        </div>
      </div>

      <!-- FOOTER TOTALS SECTION -->
      <div class="p-grid p-mt-4 p-ai-center" style="background-color: #f8f9fa; border-radius: 6px; padding: 1rem;">
        <!-- Left Side: Desconto Financeiro & Encomenda -->
        <div class="p-col-12 p-md-6">
          <div class="p-fluid p-formgrid p-grid">
            <div class="p-field p-col-12 p-md-6">
              <label for="descfin">Desconto Financeiro (%)</label>
              <InputNumber
                id="descfin"
                v-model="form.descfin"
                :min="0"
                :max="100"
                :minFractionDigits="2"
                suffix="%"
                @input="recalculateTotals"
              />
            </div>
            <div class="p-field p-col-12 p-md-6">
              <label for="encomenda">Encomenda</label>
              <InputText id="encomenda" v-model="form.encomenda" placeholder="Ref. Encomenda" />
            </div>
          </div>
        </div>

        <!-- Right Side: Totals -->
        <div class="p-col-12 p-md-6">
          <div class="p-d-flex p-flex-column p-ai-end">
            <div class="p-d-flex p-jc-between p-w-100 p-max-w-300 p-mb-2">
              <span class="text-secondary">Base de Incidência:</span>
              <strong class="p-text-bold">{{ formatCurrency(calculatedTotals.totbase) }}</strong>
            </div>

            <div class="p-d-flex p-jc-between p-w-100 p-max-w-300 p-mb-2">
              <span class="text-secondary">Total de IVA:</span>
              <strong class="p-text-bold">{{ formatCurrency(calculatedTotals.totiva) }}</strong>
            </div>

            <div class="p-d-flex p-jc-between p-w-100 p-max-w-300 p-py-2 p-border-top" style="font-size: 1.2rem;">
              <span>Total em Euros:</span>
              <strong class="p-text-bold text-primary">{{ formatCurrency(calculatedTotals.total) }}</strong>
            </div>
          </div>
        </div>
      </div>

      <!-- FORM SUBMIT BUTTONS -->
      <div class="p-mt-4 p-d-flex p-jc-between">
        <Button
          type="button"
          label="Voltar"
          icon="pi pi-arrow-left"
          class="p-button-secondary"
          @click="goBack"
        />
        <Button
          type="submit"
          :label="isEditMode ? 'Atualizar Compra' : 'Guardar Compra'"
          icon="pi pi-check"
          class="p-button-success"
          :loading="loading"
          :disabled="submitting || (isEditMode && !purchaseLoaded)"
        />
      </div>
    </form>

  </div>
</template>

<script>
import purchaseService from "@/services/purchase.service";
import supplierService from "@/services/supplier.service";
import stockService from "@/services/stock.service";
import AppAutocomplete from "@/components/elements/AppAutocomplete.vue";
import PurchaseFiles from "@/components/purchases/PurchaseFiles.vue";
import Loading from "vue-loading-overlay";
import TabView from 'primevue/tabview';
import TabPanel from 'primevue/tabpanel';

export default {
  name: "PurchaseForm",
  components: { AppAutocomplete, PurchaseFiles, Loading, TabView, TabPanel },
  props: {
    stamp: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      loading: false,
      submitting: false,
      purchaseLoaded: false,
      isEditMode: false,
      docTypes: [
        { id: 1, name: "V/Fatura", code: "C" },
        { id: 2, name: "Nota de Encomenda", code: "NE" },
        { id: 3, name: "Guia de Receção", code: "GR" },
        { id: 4, name: "Fatura / Recibo", code: "FR" },
      ],
      currencies: [
        { id: "EURO", name: "EURO" }
      ],
      investments: [
        { id: "Nao", name: "Não" },
        { id: "Sim", name: "Sim" },
      ],
      productSuggestions: [],
      listVats: [],
      warehouses: [],
      stagedFiles: [],
      form: {
        no: null,
        nome: "",
        ndoc: 1,
        nmdoc: "V/Fatura",
        docnome: "C",
        nfact: "",
        dataopen: new Date(),
        datadoc: null,
        vencimento: null,
        moeda: "EURO",
        investimento: "Nao",
        descfin: 0,
        encomenda: "",
        status: "Por Regularizar.",
        totbase: 0,
        totiva: 0,
        total: 0,
        lines: [],
      },
      calculatedTotals: {
        totbase: 0,
        totiva: 0,
        total: 0,
      },
      formErrors: {},
    };
  },
  async created() {
    this.loading = true;
    await this.fetchOptions();
    await this.fetchWarehouses();
    await this.fetchVats();
    if (this.stamp) {
      this.isEditMode = true;
      await this.fetchPurchase();
    } else {
      // Add default empty line
      this.addLine();
    }
    this.recalculateTotals();
    this.loading = false;
  },
  methods: {
    async fetchOptions() {
      try {
        const opts = await purchaseService.getFormOptions();

        if (opts.doc_types && opts.doc_types.length) this.docTypes = opts.doc_types;
        if (opts.currencies && opts.currencies.length) this.currencies = opts.currencies;
        if (opts.investments && opts.investments.length) this.investments = opts.investments;
      } catch (err) {
        console.error("Error loading options:", err);
      }
    },

    async fetchWarehouses() {
      try {
        const response = await stockService.getStorages();
        this.warehouses = (response.data || []).map((warehouse) => ({
          reference: warehouse.no,
          name: warehouse.nome,
        }));
      } catch (err) {
        console.error("Error loading warehouses:", err);
        this.warehouses = [];
      }
    },

    async fetchVats() {
      try {
        const response = await stockService.getListVats();
        this.listVats = response.data || response || [];
      } catch (err) {
        console.error("Error loading VATs:", err);
        this.listVats = [];
      }
    },

    async searchProduct(event) {
      try {
        const query = event.query;
        const response = await stockService.getQueryProducts({ search: query });
        if (response && response.data) {
          this.productSuggestions = response.data.data || response.data;
        } else {
          this.productSuggestions = [];
        }
      } catch (error) {
        console.error("Erro ao buscar produtos:", error);
        this.productSuggestions = [];
      }
    },

    onProductSelect(product, index) {
      const line = this.form.lines[index];
      if (!line || !product) return;

      line.ref = product.code || product.ref || line.ref;
      line.design = product.description || product.design || line.design;

      if (product.price_cost !== undefined && product.price_cost !== null && product.price_cost > 0) {
        line.preco = parseFloat(product.price_cost);
      } else if (product.sale_price !== undefined && product.sale_price !== null && product.sale_price > 0) {
        line.preco = parseFloat(product.sale_price);
      }

      if (product.vat !== undefined && product.vat !== null && product.vat != 0) {
        line.iva = product.vat;
        const selectedVat = this.listVats.find((v) => String(v.code) === String(product.vat));
        if (selectedVat) {
          line.taxaiva = parseFloat(selectedVat.value || 0);
        }
      } else {
        const defaultVat = this.listVats.find((v) => v.value === 23) || this.listVats[0] || { code: 2, value: 23 };
        line.iva = defaultVat.code;
        line.taxaiva = parseFloat(defaultVat.value || 0);
      }

      this.recalculateTotals();
    },

    onVatChange(line) {
      const selectedVat = this.listVats.find((v) => String(v.code) === String(line.iva));
      if (selectedVat) {
        line.taxaiva = parseFloat(selectedVat.value || 0);
      }
      this.recalculateTotals();
    },

    getWarehouseLabel(reference) {
      const warehouse = this.warehouses.find(
        (item) => String(item.reference) === String(reference)
      );
      return warehouse
        ? `${warehouse.reference} - ${warehouse.name}`
        : String(reference);
    },

    async fetchPurchase() {
      try {
        const res = await purchaseService.getPurchase(this.stamp);
        if (res) {
          this.form = {
            ...res,
            no: res.no ? Number(res.no) : null,
            dataopen: res.dataopen ? new Date(res.dataopen) : new Date(),
            datadoc: res.datadoc ? new Date(res.datadoc) : null,
            vencimento: res.vencimento ? new Date(res.vencimento) : null,
            lines: (res.lines || []).map((l) => {
              let vatCode = l.tabiva !== undefined && l.tabiva !== null ? Number(l.tabiva) : (l.iva !== undefined && l.iva !== null ? Number(l.iva) : 2);
              if (vatCode > 9 && this.listVats && this.listVats.length) {
                const matchedVat = this.listVats.find((v) => parseFloat(v.value) === parseFloat(vatCode));
                if (matchedVat) vatCode = Number(matchedVat.code);
              }
              return {
                ...l,
                ref: l.ref || l.product_ref || "",
                design: l.design || l.description || "",
                qtt: parseFloat(l.qtt || l.quantity || 0),
                preco: parseFloat(l.preco || l.price_cost || l.sale_price || 0),
                desconto: parseFloat(l.desconto || l.discount || 0),
                desc2: parseFloat(l.desc2 || 0),
                armazem: l.armazem || 50,
                iva: vatCode,
                taxaiva: parseFloat(l.taxaiva ?? 23),
                ivaincl: Boolean(l.ivaincl),
                desp: Boolean(l.desp),
              };
            }),
          };
          this.purchaseLoaded = true;
        }
      } catch (err) {
        this.purchaseLoaded = false;
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: "Não foi possível carregar a compra.",
          life: 3000,
        });
      }
    },

    async fetchSuppliers(search = "") {
      return (await supplierService.query({ search })) || [];
    },

    async getCurrentSupplier(no) {
      if (!no) return null;
      const suppliers = await supplierService.query({ no });
      return suppliers && suppliers.length ? suppliers[0] : null;
    },

    getSupplierLabel(supplier) {
      if (!supplier) return "";
      return `#${supplier.id} - ${supplier.name} | NIF: ${supplier.nif || ""}`;
    },

    async onSupplierChange(no) {
      const supplier = await this.getCurrentSupplier(no);
      this.form.nome = supplier ? supplier.name : "";
    },

    clearSupplier() {
      this.form.no = null;
      this.form.nome = "";
    },

    onDocTypeChange(e) {
      const dt = this.docTypes.find((d) => d.name === e.value);
      if (dt) {
        this.form.ndoc = dt.id;
        this.form.docnome = dt.code;
      }
    },

    addLine() {
      const defaultVat = this.listVats.find((v) => v.value === 23) || this.listVats[0] || { code: 2, value: 23 };
      this.form.lines.push({
        ref: "",
        design: "",
        qtt: 1,
        preco: 0,
        desconto: 0,
        desc2: 0,
        total: 0,
        armazem: 50,
        iva: defaultVat.code,
        taxaiva: defaultVat.value,
        ivaincl: false,
        desp: false,
      });
      this.recalculateTotals();
    },

    removeLine(index) {
      this.form.lines.splice(index, 1);
      this.recalculateTotals();
    },

    computeLineTotal(line) {
      const qtt = parseFloat(line.qtt || 0);
      const preco = parseFloat(line.preco || 0);
      const desc1 = parseFloat(line.desconto || 0);
      const desc2 = parseFloat(line.desc2 || 0);
      const taxaIva = parseFloat(line.taxaiva || 0);

      const net = qtt * preco * (1 - desc1 / 100) * (1 - desc2 / 100);
      const totalLine = line.ivaincl ? net * (1 + taxaIva / 100) : net;
      return totalLine;
    },

    recalculateTotals() {
      let totbase = 0;
      let totiva = 0;

      this.form.lines.forEach((line) => {
        const qtt = parseFloat(line.qtt || 0);
        const preco = parseFloat(line.preco || 0);
        const desc1 = parseFloat(line.desconto || 0);
        const desc2 = parseFloat(line.desc2 || 0);
        const taxaIva = parseFloat(line.taxaiva || 0);

        const discountedAmount = qtt * preco * (1 - desc1 / 100) * (1 - desc2 / 100);
        const lineNet = discountedAmount;
        const lineIva = line.ivaincl ? lineNet * (taxaIva / 100) : 0;

        totbase += lineNet;
        totiva += lineIva;
      });

      const descFin = parseFloat(this.form.descfin || 0);
      const subAfterDescFin = totbase * (1 - descFin / 100);
      const vatAfterDescFin = totiva * (1 - descFin / 100);
      const grandTotal = subAfterDescFin + vatAfterDescFin;

      this.calculatedTotals = {
        totbase: Math.round(totbase * 100) / 100,
        totiva: Math.round(vatAfterDescFin * 100) / 100,
        total: Math.round(grandTotal * 100) / 100,
      };

      this.form.totbase = this.calculatedTotals.totbase;
      this.form.totiva = this.calculatedTotals.totiva;
      this.form.total = this.calculatedTotals.total;
    },

    formatCurrency(val) {
      const currency = this.form.moeda === "EURO" ? "EUR" : (this.form.moeda || "EUR");
      return new Intl.NumberFormat("pt-PT", {
        style: "currency",
        currency,
      }).format(val || 0);
    },

    formatDate(dateStr) {
      if (!dateStr) return "-";
      return String(dateStr).slice(0, 10);
    },

    formatDateForApi(d) {
      if (!d) return null;
      if (typeof d === "string") return d;
      const yr = d.getFullYear();
      const mo = String(d.getMonth() + 1).padStart(2, "0");
      const dy = String(d.getDate()).padStart(2, "0");
      return `${yr}-${mo}-${dy}`;
    },

    validateForm() {
      this.formErrors = {};
      if (!this.form.no) this.$set(this.formErrors, "no", "Fornecedor é obrigatório.");
      if (!this.form.nmdoc) this.$set(this.formErrors, "nmdoc", "Tipo de documento é obrigatório.");
      if (!this.form.nfact) this.$set(this.formErrors, "nfact", "Nº do documento é obrigatório.");
      if (!this.form.lines.length) this.$set(this.formErrors, "lines", "Adicione pelo menos uma linha.");
      return Object.keys(this.formErrors).length === 0;
    },

    async submitForm() {
      if (this.submitting || (this.isEditMode && !this.purchaseLoaded)) return;

      if (!this.validateForm()) {
        this.$toast.add({
          severity: "warn",
          summary: "Validação",
          detail: "Preencha todos os campos obrigatórios.",
          life: 3000,
        });
        return;
      }

      this.loading = true;
      this.submitting = true;

      const payload = {
        ...this.form,
        dataopen: this.formatDateForApi(this.form.dataopen),
        datadoc: this.formatDateForApi(this.form.datadoc),
        vencimento: this.formatDateForApi(this.form.vencimento),
        totbase: this.calculatedTotals.totbase,
        totiva: this.calculatedTotals.totiva,
        total: this.calculatedTotals.total,
        lines: this.form.lines.map((l) => ({
          ...l,
          total: this.computeLineTotal(l),
        })),
      };

      try {
        if (this.isEditMode) {
          await purchaseService.updatePurchase(this.stamp, payload);
          this.$toast.add({
            severity: "success",
            summary: "Sucesso",
            detail: "Compra atualizada com sucesso!",
            life: 3000,
          });
        } else {
          const response = await purchaseService.createPurchase(payload);

          const purchaseStamp = response?.purchase?.fostamp;
          if (!purchaseStamp) {
            throw new Error("A API não devolveu a referência da compra criada.");
          }

          try {
            await this.uploadStagedFiles(purchaseStamp);
          } catch (uploadError) {
            this.$toast.add({
              severity: "warn",
              summary: "Compra criada",
              detail: "A compra foi criada, mas nem todos os ficheiros foram enviados. Pode voltar a adicioná-los na edição.",
              life: 6000,
            });
            await this.$router.push({
              name: "PurchaseEdit",
              params: { stamp: purchaseStamp },
            });
            return;
          }

          this.$toast.add({
            severity: "success",
            summary: "Sucesso",
            detail: this.stagedFiles.length
              ? `Compra criada e ${this.stagedFiles.length} ficheiro(s) enviado(s) com sucesso!`
              : "Compra criada com sucesso!",
            life: 3000,
          });
        }
        this.$closeCurrentTab({ name: 'Purchases' });
      } catch (err) {
        this.$toast.add({
          severity: "error",
          summary: "Erro",
          detail: err.response?.data?.message || "Ocorreu um erro ao guardar.",
          life: 4000,
        });
      } finally {
        this.loading = false;
        this.submitting = false;
      }
    },

    async uploadStagedFiles(stamp) {
      for (const entry of this.stagedFiles) {
        const formData = new FormData();
        formData.append("file", entry.file);
        formData.append("resume", entry.description);
        await purchaseService.uploadPurchaseFile(stamp, formData);
      }
    },

    goBack() {
      this.$closeCurrentTab({ name: 'Purchases' });
    },
  },
};
</script>

<style scoped>
.p-table-responsive {
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
}

.custom-purchases-table {
  width: 100%;
  min-width: 1400px;
  border-collapse: collapse;
  margin-top: 0.5rem;
}

.custom-purchases-table th {
  background-color: #f4f6f9;
  color: #495057;
  padding: 8px 10px;
  font-weight: 600;
  border-bottom: 2px solid #dee2e6;
  text-align: left;
}

.custom-purchases-table td {
  padding: 6px 8px;
  border-bottom: 1px solid #e9ecef;
}

.p-w-100 {
  width: 100%;
}

.p-max-w-300 {
  max-width: 300px;
}

.p-border-top {
  border-top: 1px solid #ced4da;
}
</style>
